import {createFileRoute, notFound} from "@tanstack/react-router";
import {getDataSourcesForFrontend} from "~/domainModels/dag/dataSources";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {ErrorComponent} from "./-errorComponent";
import {NotFoundComponent} from "./-notFoundComponent";
import {PendingComponent} from "./-pendingComponent";
import {RouteComponent} from "./-routeComponent";
import {z as zod} from "zod";
import {DAG_ConnectionAttemptStatus} from "~/domainModels/dag/dataSourceConnectionAttempt";
import {generateUuid, getCurrentIsoTimestamp} from "@vesta/global-common-typescript/utilities/utilities";
import {dataSourceConnectionStrategyDetailsMap} from "~/domainModels/dag/dataSourceConnectionStrategy";
import {throwRedirectToConfigDataSourceDetailWhenPresent} from "~/workspaceConfig/configOttosyncRedirects";

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottosync/_ottosyncScaffold/data-sources/$dataSourceId",
)({
    validateSearch: zod.object({
        connectionAttemptId: uuidZodValidator.optional(),
    }),
    // loaderDeps: () => ({}),
    beforeLoad: ({context, params, search}) => {
        throwRedirectToConfigDataSourceDetailWhenPresent({
            workspaceId: context.workspace.id,
            dataSourceId: params.dataSourceId,
            connectionAttemptId: search.connectionAttemptId,
        });
        return {
            connectionAttemptId: search.connectionAttemptId,
        };
    },
    loader: async ({context, params}) => {
        const dataSourceIdResult = uuidZodValidator.safeParse(params.dataSourceId);
        if (dataSourceIdResult.success === false) {
            throw notFound();
        }
        const dataSourceId = dataSourceIdResult.data;

        const dataSourcesResult = await getDataSourcesForFrontend({
            accessToken: context.authenticationStatus.accessToken,
            intellsysBaseUrl: context.intellsysBaseUrl,
            workspaceId: context.workspace.id,
            filters: {
                id: dataSourceId,
            },
        });
        if (dataSourcesResult.success !== true) {
            throw dataSourcesResult.error;
        }
        const dataSources = dataSourcesResult.data;
        if (dataSources.length === 0) {
            throw notFound();
        }
        const dataSource = dataSources[0]!;

        // #region Refresh connection attempts from local storage
        context.connectionAttemptClient.refreshFromLocalStorage();
        // #endregion

        if (context.connectionAttemptId !== undefined) {
            const connectionAttempt = context.connectionAttemptClient.getConnectionAttempt(context.connectionAttemptId);
            if (connectionAttempt === undefined) {
                throw new Error("Connection attempt not found");
            }
            if (connectionAttempt.status === DAG_ConnectionAttemptStatus.inProgress) {

                const dataSource = dataSources.find((dataSource) => dataSource.id === connectionAttempt.dataSourceId);
                if (dataSource === undefined) {
                    throw new Error("c3256870-18f5-40bb-b3a3-b64d16220664");
                }
                const dataSourceConnectionStrategyDetails = dataSourceConnectionStrategyDetailsMap[dataSource.connectionStrategy];

                const currentStep = dataSourceConnectionStrategyDetails.connectionSteps.find((step) => step.id === connectionAttempt.currentStepId);
                if (currentStep === undefined) {
                    throw new Error("371d404e-664a-4a9b-95b2-94e594dd25af");
                }
                if (currentStep.isResumable === false) {
                    const connectionAttemptUpdateResult = context.connectionAttemptClient.setConnectionAttempt({
                        ...connectionAttempt,
                        updatedAt: getCurrentIsoTimestamp(),
                        revisionId: generateUuid(),
                        status: DAG_ConnectionAttemptStatus.failed,
                    });
                    if (connectionAttemptUpdateResult.success !== true) {
                        throw connectionAttemptUpdateResult.error;
                    }
                }
            }
        }

        return {
            dataSource: dataSource,
            connectionAttemptId: context.connectionAttemptId === undefined ? null : context.connectionAttemptId,
            intellsysBaseUrl: context.intellsysBaseUrl,
            accessToken: context.authenticationStatus.accessToken,
            workspaceId: context.workspace.id,
            //
        };
    },
    component: RouteComponent,
    errorComponent: ErrorComponent,
    pendingComponent: PendingComponent,
    notFoundComponent: NotFoundComponent,
});
